import { isSelfSignupClientName } from '@revelio/core/self-signup-plan';
import { MetadataKey, TrialType, type User } from '@revelio/data-access';

export const isValidEmail = (email: string): boolean => {
  return !!/\S+@\S+\.\S+/.test(email || '') && !email.includes(' ');
};

export const hasUppercaseLetter = (password: string): boolean => {
  return !!password.match(/[A-Z]/g);
};

export const hasNumberOrSpecialCharacter = (password: string): boolean => {
  return !!password.match(/[0-9]|(?=.*?[#?!@$%^&*-:()])/g);
};

export const isValidPassword = (password: string): boolean => {
  return !!(
    password.length > 7 &&
    hasNumberOrSpecialCharacter(password) &&
    hasUppercaseLetter(password)
  );
};

export const isUserTrialExpired = (user: User) => {
  if (user.live) return false;
  const expirationDate = user?.metadata?.find(
    (m) => m?.key === MetadataKey.ExpirationDate
  )?.value;
  if (!expirationDate) return false;
  return new Date(expirationDate) < new Date();
};

/**
 * Whether the user is a self-signup (free trial) account, derived SYNCHRONOUSLY
 * from the user object.
 *
 * `useUserTrialDetails` exposes the same answer, but only after its effect has
 * run — too late for callers that act during render or in a layout effect (the
 * AI assistant applies filters in one, and read `undefined` there). Those
 * callers should read this off `useGetLoggedInUser().loggedInUser`, which is a
 * memo and already has impersonation overrides applied.
 *
 * Mirrors `useUserTrialDetails` exactly: a non-empty user with an ABSENT
 * `client_name` counts as self-signup, and an empty (pre-auth) user does not.
 * `client_name: null` does NOT — `client_name` is a nullable GraphQL field, so
 * a live client can come back as an explicit null, and the lodash `get` default
 * this replaces only substituted for `undefined`. Treating null as self-signup
 * would restrict a paying user's AI company filters and grey out their company
 * picker.
 */
export const isSelfSignupUser = (user: User | undefined | null): boolean => {
  if (!user || Object.keys(user).length === 0) {
    return false;
  }
  return isSelfSignupClientName(user.client_name);
};

export const getUserTrialType = (user: User): TrialType | false => {
  if (user.live) return false;
  return (
    (user?.metadata?.find((m) => m?.key === MetadataKey.TrialType)
      ?.value as TrialType) || TrialType.ONE_YEAR_DATA_LAG
  );
};
